import PropTypes from 'prop-types';
import React, {useContext} from 'react';
import {TableColumn} from "../../tableSettings";
import {TextAlignLeftRenderer} from "../SmartTableRenderers";
import {SQLIndex} from "../../fms.core/constants";
import ApplicationContext from "../../ApplicationContext";
import LookupField from "../../ui/lookup/LookupField";
import {CompanyLookupProps} from "./CompanyLookupField";
import {LookupAccountCode} from "../../fms.data/lookup";

interface NewCountryLookupProps extends Omit<CompanyLookupProps, 'onSelect'>{
  onSelect: (x: LookupAccountCode) => void,
  glType : string,
  opType : string,
  subType : string,
  searchStartWith : boolean
}

const columns: Array<TableColumn> = [
  {
    Header: 'Account Code',
    accessor: 'code',
    resizable: true,
    sortable: false,
    canBeHidden: false,
    visible: true,
    width: 100,
    Cell: TextAlignLeftRenderer
  },
  {
    Header: 'Account Name',
    accessor: 'name',
    resizable: true,
    sortable: false,
    canBeHidden: false,
    visible: true,
    width: 280,
    Cell: TextAlignLeftRenderer
  },
];

const GlNoLookupField = ({
    initialValue = '',
    onSelect = () => {
    },
    placeholder = undefined,
    onTextChange = () => {
    },
    disabled = false,
    lookupId = undefined,
    onTabNext = undefined,
    onTabPrevious = undefined,
    companyItems = undefined,
    onFocus = () => {
    },
    onBlur = () => {
    },
    focusableWhenDisabled = false,
    glType = '',
    opType = '',
    subType = '',
    searchStartWith = false,
    ...others
  }: NewCountryLookupProps) => {

  const appContext = useContext(ApplicationContext);

  async function queryCompanies(queryString: string | null) {
    const apiResult = await appContext.api.getLookupItems(SQLIndex.GLNo, queryString);
    let data = apiResult?.entities ?? [];

    if (glType === 'cost') {
      data = data.filter((x: LookupAccountCode) => x.glType === 'C');

      if (subType === 'DS')
        data = data.filter((x: LookupAccountCode) => x.glSubGroup === 'DS');
      else if (subType === 'IDS')
        data = data.filter((x: LookupAccountCode) => x.glSubGroup === 'IDS');
      else if (subType === 'TS')
        data = data.filter((x: LookupAccountCode) => x.glSubGroup === 'TS');
    }
    else if (glType === 'sales') {
      data = data.filter((x: LookupAccountCode) => x.glType === 'I');

      if (subType === 'DS')
        data = data.filter((x: LookupAccountCode) => x.glSubGroup === 'DS');
      else if (subType === 'IDS')
        data = data.filter((x: LookupAccountCode) => x.glSubGroup === 'IDS');
      else if (subType === 'TS')
        data = data.filter((x: LookupAccountCode) => x.glSubGroup === 'TS');
    } else if (glType === 'expense') {
      data = data.filter((x: LookupAccountCode) => (x.code.startsWith('431') || x.code.startsWith('45')));
    } else if (glType === 'income') {
      data = data.filter((x: LookupAccountCode) => x.code.startsWith('55'));
    } else if (glType === 'generalCost') {  
      data = data.filter((x: LookupAccountCode) => (x.code.startsWith('431') || x.code.startsWith('45') || x.code.startsWith('55')|| x.code.startsWith('1')));
    } else if (glType === 'generalSales') { 
      data = data.filter((x: LookupAccountCode) => (x.code.startsWith('431') || x.code.startsWith('45') || x.code.startsWith('55')|| x.code.startsWith('2')));
    } else if (glType === 'payroll') {
      data = data.filter((x: LookupAccountCode) => ['431210', '431220', '431230', '431310'].indexOf(x.code) >= 0);
    }

    switch (opType) {
      case 'OI':
      data = data.filter((x: LookupAccountCode) => x.glGroup.startsWith('I'));
      break;
      case 'OE':
      data = data.filter((x: LookupAccountCode) => x.glGroup.startsWith('E'));
      break;
      case 'AI':
      data = data.filter((x: LookupAccountCode) => x.glGroup.startsWith('A'));
      break;
      case 'AE':
      data = data.filter((x: LookupAccountCode) => x.glGroup.startsWith('P'));
      break;
      case 'WH':
      data = data.filter((x: LookupAccountCode) => x.glGroup.startsWith('W'));
      break;
      case 'OT':
      data = data.filter((x: LookupAccountCode) => x.glGroup.startsWith('T'));
      break;
      case 'OD':
      data = data.filter((x: LookupAccountCode) => x.glGroup.startsWith('R'));
      break;
      default:
        break;
    }

    if(searchStartWith && queryString)
      data = data.filter((x: LookupAccountCode) => x.code.startsWith(queryString));

    return apiResult?.success ? data : [];
  }

  function onCancel() {
  }

  function filter(data: Array<LookupAccountCode>, filterText: string) {
    return data.findIndex(x => x.code && x.code.toLowerCase().startsWith(filterText.toLowerCase()));
  }

  return (
    <LookupField
      lookupId={lookupId}
      onTabNext={onTabNext}
      onTabPrevious={onTabPrevious}
      disabled={disabled}
      query={queryCompanies}
      width="399px"
      columns={columns}
      onSelect={onSelect}
      onCancel={onCancel}
      labelProp="code"
      initialValue={initialValue}
      placeholder={placeholder}
      onTextChange={onTextChange}
      filter={filter}
      onBlur={onBlur}
      onFocus={onFocus}
      focusableWhenDisabled={focusableWhenDisabled}
      {...others}
    />
  );
}

GlNoLookupField.defaultProps = {
  onSelect: () => {
  },
  onTextChange: () => {
  },
  filterName: 'code',
  disabled: false,
  focusableWhenDisabled: false,
  opType: '',
  glType: '',
  subType : '',
  searchStartWith : false
};

GlNoLookupField.propTypes = {
  initialValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onTextChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  filterName: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  lookupId: PropTypes.string,
  onTabNext: PropTypes.func,
  onTabPrevious: PropTypes.func,
  companyItems: PropTypes.array,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  focusableWhenDisabled: PropTypes.bool.isRequired,
  opType: PropTypes.string,
  glType: PropTypes.string,
  subType: PropTypes.string,  
  searchStartWith: PropTypes.bool,
};


export default React.memo(GlNoLookupField);